import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminTotpSetup, adminTotpVerify } from '../../api/adminApi';
import { useAdminAuth } from '../../Context/AdminAuthContext';

export default function AdminTotpSetup() {
    const { preAuthToken, loginAdmin } = useAdminAuth();
    const navigate = useNavigate();

    const [qrUri, setQrUri] = useState('');
    const [secret, setSecret] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!preAuthToken) { navigate('/admin/login'); return; }
        adminTotpSetup(preAuthToken)
            .then(data => { setQrUri(data.qrUri); setSecret(data.secret); setBackupCodes(data.backupCodes); })
            .catch(err => setError(err.message ?? 'Setup failed'));
    }, [preAuthToken, navigate]);

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!preAuthToken) return;
        setError('');
        setLoading(true);
        try {
            const result = await adminTotpVerify(preAuthToken, code);
            loginAdmin(result.token);
            navigate('/admin');
        } catch {
            setError('Invalid code — try again');
        } finally {
            setLoading(false);
        }
    };

    const copySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2 }}>
            <Paper sx={{ p: 4, width: 480 }} elevation={3}>
                <Typography variant="h5" fontWeight={700} mb={1}>Set up 2FA</Typography>
                <Typography color="text.secondary" mb={3} variant="body2">
                    Scan the QR code with Google Authenticator, then enter the 6-digit code to confirm.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {qrUri && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <QRCodeSVG value={qrUri} size={200} />
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{secret}</Typography>
                    <Tooltip title={copied ? 'Copied!' : 'Copy secret'}>
                        <IconButton size="small" onClick={copySecret}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" fontWeight={700} mb={1}>Backup codes — save these now</Typography>
                <Typography variant="caption" color="error" display="block" mb={1}>
                    These are shown only once. Store them securely.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
                    {backupCodes.map(c => (
                        <Chip key={c} label={c} size="small" sx={{ fontFamily: 'monospace' }} />
                    ))}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box component="form" onSubmit={handleVerify}>
                    <TextField
                        label="Enter 6-digit code to confirm"
                        fullWidth
                        required
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        sx={{ mb: 2 }}
                    />
                    <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
                        {loading ? <CircularProgress size={22} /> : 'Activate 2FA & Login'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
